<script>
import DatePicker from "@/components/_Shared/DatePicker";
import TimePicker from "@/components/_Shared/TimePicker";
import ConfirmPeriodAndPrice from "./Steps/ConfirmPeriodAndPrice";
import Professionals from "./Steps/Professionals";
import ProfessionalsSuggestion from "./Steps/ProfessionalsSuggestion";
import CheckoutPopup from "./Steps/CheckoutPopup/Index";
import CovidNotice from "@/components/PurchaseFlow/CovidNotice.vue";

import scrollMixin from "@/mixins/scrollMixin";
import timeMixin from "@/mixins/timeMixin";

import eventHelper from "@/utils/helpers/event";
import holidayHelper from "@/utils/helpers/holiday";
import moment from "@/utils/moment";
import { mapState, mapActions, mapGetters } from "vuex";

export default {
  mixins: [scrollMixin, timeMixin],

  data() {
    return {
      fromDate: moment()
        .add(35, "days")
        .toDate(),
      toDate:
        moment().hours() < 18
          ? moment()
              .startOf("day")
              .toDate()
          : moment()
              .add(1, "days")
              .startOf("day")
              .toDate(),
      showTimePicker: false,
      timeoutPopup: null,
      popupIsVisible: false,
      tomorrow: moment()
        .add(2, "days")
        .toDate()
    };
  },

  components: {
    DatePicker,
    TimePicker,
    ConfirmPeriodAndPrice,
    Professionals,
    ProfessionalsSuggestion,
    CheckoutPopup,
    CovidNotice
  },

  computed: {
    ...mapState("newOrder", [
      "address",
      "day",
      "starts_at",
      "ends_at",
      "price",
      "professional",
      "professionals_available",
      "additional_informations"
    ]),
    ...mapState("professionalsSuggestion", [
      "selectedEvent",
      "availableEvents"
    ]),
    ...mapGetters("newOrder", ["isToday"]),
    professionalsSuggestionLoader() {
      return this.$store.state.professionalsSuggestion.loader;
    }
  },

  methods: {
    ...mapActions("newOrder", ["setDay", "setStartsAt", "setEndsAt"]),
    ...mapActions("professionalsSuggestion", ["setEvent"]),

    setNextAvailableDay() {
      this.setEvent(null);

      let nextAvailableDay = moment();
      let holiday = null;

      do {
        nextAvailableDay = nextAvailableDay.add(1, "days");

        holiday = holidayHelper.findHoliday(nextAvailableDay.toDate());
      } while (holiday !== undefined && !holiday.activePurchase);

      this.setDay(nextAvailableDay.toDate());
    },

    selectDay(day) {
      this.setEvent(null);
      this.setDay(day);
      this.showTimePicker = true;
      this.scrollTo("#time-picker");
    },

    selectSuggestionDay(day) {
      this.setEvent(null);
      this.setDay(day);
      this.showTimePicker = true;
      this.scrollTo("#time-picker");
    },

    getHourOfDate(date) {
      return parseInt(moment(date).format("HH"));
    },

    selectStartsAt(startsAt) {
      this.setStartsAt(this.$options.filters.intToTime(startsAt));
    },

    selectEndsAt(EndsAt) {
      this.setEndsAt(this.$options.filters.intToTime(EndsAt));
      this.scrollTo("#confirm-period-and-price");
      this.showTimePicker = false;
    },

    getTimePickerInitialHour() {
      if (
        eventHelper.isUserInSleepingTime() &&
        (moment().hour() > 19 && moment().hour() < 24) &&
        eventHelper.isTomorrow(this.day)
      ) {
        return 10;
      }

      if (eventHelper.isUserInSleepingTime() && eventHelper.isToday(this.day)) {
        return 10;
      }

      return 7;
    },

    getTimePickerFinalHour() {
      const startsAt = this.starts_at;
      const holiday = !startsAt && holidayHelper.findHoliday(this.day);

      return holiday && holiday.activePurchase && holiday.max_starts_at
        ? holiday.max_starts_at
        : 22;
    },

    setTimePickerInitialHour() {
      let startsAt = this.selectedEvent && this.selectedEvent.starts_at;
      return startsAt
        ? this.getHourOfDate(startsAt)
        : this.getTimePickerInitialHour();
    },

    setTimePickerFinalHour() {
      let endsAt = this.selectedEvent && this.selectedEvent.ends_at;
      return endsAt
        ? this.getHourOfDate(endsAt)
        : this.getTimePickerFinalHour();
    }
  },

  mounted() {
    this.setNextAvailableDay();
  }
};
</script>

<template>
  <div class="section" id="PerSchedule">
    <div class="section-body">

      <covid-notice />

      <transition name="fade" mode="out-in">
        <date-picker
          title="ESCOLHA O MELHOR DIA E HORÁRIO"
          :day="day"
          :fromDate="fromDate"
          :toDate="toDate"
          :selectedDate="day"
          :class="{'passed-on': ends_at}"
          :address="address"
          @daySelected="selectDay"
        />
      </transition>

      <transition name="fade" mode="out-in">
        <time-picker
          id="time-picker"
          v-show="day"
          :visible="showTimePicker"
          :isToday="isToday"
          :startsAt="parseInt(starts_at)"
          :endsAt="parseInt(ends_at)"
          :initialHour="setTimePickerInitialHour()"
          :finalHour="setTimePickerFinalHour()"
          :class="{'passed-on': ends_at}"
          @startSelected="selectStartsAt"
          @endSelected="selectEndsAt"
        />
      </transition>

      <transition name="fade" mode="out-in">
        <confirm-period-and-price
          id="confirm-period-and-price"
          v-if="price"
          :class="{'passed-on': additional_informations}"
          @scrollTo="scrollTo('#confirm-period-and-price')"
        />
      </transition>

      <transition name="fade" mode="out-in">
        <professionals
          id="professionals"
          v-if="ends_at"
          title="PROFISSIONAIS DISPONÍVEIS"
          :class="{'passed-on': additional_informations}"
          @professionalSelected="$emit('scrollTo', '#additional-informations')"
        />
      </transition>

      <transition name="fade" mode="out-in">
        <professionals-suggestion
          id="professionals-suggestions"
          v-if="professionalsSuggestionLoader || availableEvents"
          title="HORÁRIOS SUGERIDOS"
          :class="{'passed-on': professional}"
          @daySelected="selectSuggestionDay"
        />
      </transition>

      <transition name="fade" mode="out-in">
        <checkout-popup v-if="popupIsVisible" @close="popupIsVisible = false" />
      </transition>
    </div>
  </div>
</template>

<style scoped>
.professional-suggestions {
  max-width: 100%;
}

.schedule {
  padding: 10px 20px 10px 50px;
  background-position: 15px center;
  background-size: 20px;
  background-repeat: no-repeat;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  margin-bottom: 10px;
  user-select: none;
}

.section.passed-on .passed-on {
  opacity: 1;
}

.passed-on {
  opacity: 0.35;
}

.passed-on:hover {
  opacity: 1;
}

.section-body {
  width: 100%;
}

.date {
  background-image: url("/static/icon-date.svg");
}
</style>



// WEBPACK FOOTER //
// src/components/PurchaseFlow/PerSchedule.vue